import { BodyText, Button, HeadingText } from '@unmind/design-system';
import { FontSizes } from '@unmind/design-system/theme/deprecated';
import RoutePath from 'App/RoutePath';
import { tracking } from 'App/Tracking';
import { practitionerMatchesQuery_practitionerMatches } from 'Services/BeGateway/Practitioner/__generated__/practitionerMatchesQuery';
import { practitionersQuery_practitioners_edges_node as Practitioner } from 'Services/BeGateway/Practitioner/__generated__/practitionersQuery';
import SectionError from 'Shared/SectionError';
import { SkeletonContentTile } from 'Shared/Skeleton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { extraSmall, small } from 'utils';
import { useUserLocation } from 'Account/hooks';
import { usePractitioners } from 'Book/shared';
import { Card, Explainer } from './components';

const MatchesContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 16px;
  padding-bottom: 48px;
`;

const MatchesHeaderContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
const MatchesHeader = styled(HeadingText).attrs(({ theme }) => ({
  sizes: [FontSizes.fontSize20],
  weight: theme.typography.fontWeights.semiBold,
  level: 2,
  align: 'left',
}))`
  margin-bottom: 16px;
` as any;
/* eslint-enable @typescript-eslint/no-explicit-any */

const MatchesExplainerContainer = styled.div`
  margin-bottom: 16px;
`;

const MatchesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  margin-bottom: 40px;

  > a,
  > div {
    width: 100%;

    ${extraSmall(css`
      flex: 1 0 calc(50% - 32px);
      max-width: calc(50% - 16px);
    `)}

    ${small(css`
      flex: 1 0 calc(33.3% - 32px);
    `)}
  }
`;

const BrowseCTAContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  align-self: center;
  gap: 32px;
  padding: 20px;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 12px;
`;

const BrowseCTAHeader = styled(BodyText).attrs(({ theme }) => ({
  color: theme.colors.text.primary,
  sizes: [FontSizes.fontSize18],
}))``;

interface Props {
  matches: practitionerMatchesQuery_practitionerMatches[];
  cardOrientation?: 'horizontal' | 'vertical';
  trackingSource: 'home_match' | 'browse_match';
}

export default ({
  matches,
  cardOrientation = 'vertical',
  trackingSource,
}: Readonly<Props>) => {
  const { t } = useTranslation('book', {
    keyPrefix: 'therapyAndCoaching.matches',
  });
  const { loading: userLocationDataLoading } = useUserLocation();

  const matchIds = matches.map(match => match?.id);

  const {
    data,
    loading: practitionerMatchesLoading,
    error,
    refetch,
  } = usePractitioners({
    practitionersQueryWhere: {
      id: {
        in: matchIds,
      },
    },
    limit: 3,
    cacheConfig: 'no-cache',
  });

  const handleBrowseClick = () =>
    tracking.track('talk-home-matching-browse-clicked', {
      userHasMatches: true,
    });

  if (practitionerMatchesLoading || userLocationDataLoading) {
    return (
      <MatchesContainer>
        <MatchesHeader>{t('title')}</MatchesHeader>
        <MatchesGrid>
          {[...Array(3)].map((_, i) => (
            <div key={i}>
              <SkeletonContentTile />
            </div>
          ))}
        </MatchesGrid>
      </MatchesContainer>
    );
  }

  if (error) {
    return (
      <MatchesContainer>
        <MatchesHeader>{t('title')}</MatchesHeader>
        <SectionError onRetry={async () => refetch()} />
      </MatchesContainer>
    );
  }

  const practitionerEdges = (data?.practitioners?.edges ?? []).filter(
    (edge): edge is NonNullable<typeof edge> => edge !== null,
  );

  return (
    <MatchesContainer>
      <MatchesHeaderContainer>
        <MatchesHeader>{t('title')}</MatchesHeader>
        {cardOrientation === 'vertical' ? (
          <MatchesExplainerContainer>
            <Explainer />
          </MatchesExplainerContainer>
        ) : null}
      </MatchesHeaderContainer>

      {practitionerEdges?.length ? (
        <MatchesGrid>
          {practitionerEdges.map(edge => (
            <Card
              match={edge.node as Practitioner}
              key={edge?.node?.id}
              orientation={cardOrientation}
              trackingSource={trackingSource}
            />
          ))}
        </MatchesGrid>
      ) : null}

      {cardOrientation === 'vertical' ? (
        <BrowseCTAContainer>
          <BrowseCTAHeader>{t('subtitle')}</BrowseCTAHeader>
          <Button
            label={t('cta')}
            to={RoutePath.BookPractitioners}
            onClick={handleBrowseClick}
          />
        </BrowseCTAContainer>
      ) : null}
    </MatchesContainer>
  );
};
